import React from 'react'
import { Button } from '../common/Button'
import { useAudioRecorder } from '../../hooks/useAudioRecorder'

interface AudioRecorderProps {
  onRecordingComplete: (blob: Blob, duration: number) => void
  onRecordingStart?: () => void
  onRecordingCancel?: () => void
  disabled?: boolean
  continuousMode?: boolean
  autoStartRecording?: boolean
  variant?: 'default' | 'inline'
}

export function AudioRecorder({
  onRecordingComplete,
  onRecordingStart,
  onRecordingCancel,
  disabled = false,
  continuousMode = false,
  autoStartRecording = false,
  variant = 'default',
}: AudioRecorderProps) {
  const {
    isRecording,
    duration,
    permissionState,
    error,
    startRecording,
    stopRecording,
    cancelRecording,
    requestPermission,
  } = useAudioRecorder()

  // Auto-start recording if requested
  React.useEffect(() => {
    if (autoStartRecording && !isRecording && !disabled) {
      handleStart()
    }
  }, [autoStartRecording, disabled])

  const handleStart = async () => {
    try {
      if (permissionState !== 'granted') {
        await requestPermission()
      }

      // Enable VAD if in continuous mode
      const vadCallback = continuousMode ? () => {
        console.log('[AudioRecorder] VAD callback triggered')
        handleStop()
      } : undefined

      await startRecording({
        enableVAD: continuousMode,
        onSilenceDetected: vadCallback,
      })

      console.log('[AudioRecorder] Recording started, continuousMode:', continuousMode)
      onRecordingStart?.()
    } catch (err) {
      console.error('Failed to start recording:', err)
    }
  }

  const handleStop = async () => {
    console.log('[AudioRecorder] handleStop called')
    const result = await stopRecording()
    console.log('[AudioRecorder] stopRecording result:', !!result)
    if (result) {
      onRecordingComplete(result.blob, result.duration)
    }
  }

  const handleCancel = () => {
    cancelRecording()
    onRecordingCancel?.()
  }

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  if (variant === 'inline') {
    return (
      <div className="flex items-center space-x-2">
        {!isRecording ? (
          <Button
            onClick={handleStart}
            disabled={disabled}
            variant="outline"
            size="md"
            className="rounded-full p-3 h-12 w-12 flex items-center justify-center border-[var(--color-border)] text-[var(--color-text)] bg-[var(--color-surface)] hover:bg-[var(--color-surface-secondary)] transition-colors"
            title="Start speaking"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z"
                clipRule="evenodd"
              />
            </svg>
          </Button>
        ) : (
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-2 bg-error-50 border border-error-200 text-error-700 px-3 py-2 rounded-full">
              <span className="w-2 h-2 rounded-full bg-error-500 animate-pulse" />
              <span className="text-xs font-semibold">{formatDuration(duration)}</span>
              {continuousMode && (
                <span className="text-[10px] text-error-600">listening...</span>
              )}
            </div>
            {!continuousMode && (
              <Button
                onClick={handleCancel}
                variant="outline"
                size="sm"
                className="rounded-full border-[var(--color-border)] text-[var(--color-text)]"
              >
                Cancel
              </Button>
            )}
            {!continuousMode && (
              <Button
                onClick={handleStop}
                variant="success"
                size="sm"
                className="rounded-full shadow-sm"
              >
                Send
              </Button>
            )}
          </div>
        )}
        {error && (
          <span className="text-xs text-error-600">{error}</span>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center space-y-4">
      {error && (
        <div className="bg-error-50 border border-error-200 text-error-700 px-4 py-2 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="flex items-center space-x-4">
        {!isRecording ? (
          <Button
            onClick={handleStart}
            disabled={disabled}
            variant="primary"
            size="lg"
            className="rounded-full w-16 h-16 flex items-center justify-center shadow-lg bg-gradient-to-r from-primary-500 to-primary-600"
          >
            <svg
              className="w-8 h-8"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z"
                clipRule="evenodd"
              />
            </svg>
          </Button>
        ) : (
          <>
            {!continuousMode && (
              <Button
                onClick={handleCancel}
                variant="outline"
                size="lg"
                className="rounded-full border-[var(--color-border)] text-[var(--color-text)] bg-[var(--color-surface)] hover:bg-[var(--color-surface-secondary)] transition-colors"
              >
                Cancel
              </Button>
            )}

            <div className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-full bg-error-600 flex items-center justify-center animate-pulse">
                <div className="w-4 h-4 rounded-full bg-white" />
              </div>
              <span className="mt-2 text-sm font-medium text-gray-700">
                {formatDuration(duration)}
              </span>
              {continuousMode && (
                <span className="mt-1 text-xs text-gray-500">
                  Listening... (auto-stops on silence)
                </span>
              )}
            </div>

            {!continuousMode && (
              <Button
                onClick={handleStop}
                variant="success"
                size="lg"
                className="rounded-full shadow-md"
              >
                Send
              </Button>
            )}
          </>
        )}
      </div>

      {permissionState === 'denied' && (
        <p className="text-sm text-error-600">
          Microphone permission denied. Please enable it in your browser settings or use text input.
        </p>
      )}
    </div>
  )
}
